import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  LayoutDashboard, Building2, Users, Tags, Calendar, Truck,
  Star, Home, Gavel, Shield, Menu, X, RefreshCw, Package,
  CheckCircle2, Clock, XCircle, AlertCircle, TrendingUp, MessageCircle, Bell, CreditCard, MapPin, QrCode,
  ChevronRight, Megaphone, Target, UserCheck, ChevronDown, Phone, Rocket, Tag, Receipt,
  Percent, Wallet, Sparkles, Bot, Disc3,
} from "lucide-react";
import RyderLiveMap from "../components/RyderLiveMap";
import DropdownPortal from "../components/DropdownPortal";
import { toast } from "react-toastify";
import API from "../api/axiosConfig";
import CompanyManager        from "./admin/CompanyManager";
import AdminCompanyDetail    from "./admin/AdminCompanyDetail";
import UserManager           from "./admin/UserManager";
import UserClients           from "./admin/UserClients";
import CategoryManager       from "./admin/CategoryManager";
import QPointsManager        from "./admin/QPointsManager";
import AdminCoupons          from "./admin/AdminCoupons";
import QPointsReport         from "./admin/QPointsReport";
import LandingManager        from "./admin/LandingManager";
import AdminAuctions         from "./admin/AdminAuctions";
import AdminRyders           from "./admin/AdminRyders";
import EventManager          from "./admin/EventManager";
import WhatsAppManager       from "./admin/WhatsAppManager";
import WhatsAppInbox         from "./admin/WhatsAppInbox";
import AdminNotifications    from "./admin/AdminNotifications";
import PaymentMethodsManager    from "./admin/PaymentMethodsManager";
import DeliveryPricingManager   from "./admin/DeliveryPricingManager";
import QRGenerator              from "./admin/QRGenerator";
import PopupBannerManager      from "./admin/PopupBannerManager";
import ProspectManager         from "./admin/ProspectManager";
import MarketingCampaigns      from "./admin/MarketingCampaigns";
import MarketingAiConfig       from "./admin/MarketingAiConfig";
import MembershipPlans         from "./admin/MembershipPlans";
import SettlementsManager      from "./admin/SettlementsManager";
import WheelManager             from "./admin/WheelManager";
import MobileBottomBar from "../components/MobileBottomBar";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";

dayjs.extend(relativeTime);
dayjs.locale("es");

// ── Constantes ────────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { id: "dashboard",   label: "Dashboard",    icon: LayoutDashboard },
  { id: "prospects",   label: "Captacion",    icon: Target },
  { id: "marketing",   label: "Marketing",    icon: Rocket },
  { id: "marketing_ai", label: "IA Marketing", icon: Sparkles },
  { id: "memberships", label: "Membresías",   icon: Percent },
  { id: "settlements", label: "Comisiones",   icon: Wallet },
  { id: "companies",   label: "Empresas",     icon: Building2 },
  { id: "users_admin", label: "Admins",       icon: Shield },
  { id: "users",       label: "Usuarios",     icon: Users },
  { id: "ryders",      label: "Ryders",       icon: Truck },
  { id: "orders",      label: "Pedidos",      icon: Package },
  { id: "categories",  label: "Categorías",   icon: Tags },
  { id: "auctions",    label: "Subastas",     icon: Gavel },
  { id: "qpoints",     label: "Q-Puntos",     icon: Star },
  { id: "qpoints_report", label: "Reporte Q-Puntos", icon: Receipt },
  { id: "coupons",     label: "Cupones",      icon: Tag },
  { id: "wheel",       label: "Ruleta",       icon: Disc3 },
  { id: "landing",     label: "Landing",      icon: Home },
  { id: "events",      label: "Eventos",      icon: Calendar },
  { id: "whatsapp",       label: "WhatsApp",        icon: MessageCircle },
  { id: "whatsapp_inbox", label: "Multiagente WhatsApp", icon: Bot },
  { id: "notifications",  label: "Notificaciones",  icon: Bell },
  { id: "payments",         label: "Pagos",           icon: CreditCard },
  { id: "delivery_pricing", label: "Tarifas domicilio", icon: MapPin },
  { id: "qr_generator",    label: "Códigos QR",        icon: QrCode },
  { id: "popup_banner",   label: "Popup Landing",     icon: Megaphone },
];

const SECTION_LABELS = Object.fromEntries(NAV_ITEMS.map((n) => [n.id, n.label]));

const STATUS_COLORS = {
  active: "#22c55e", scheduled: "#3b82f6", ended: "#94a3b8",
  sold: "#f59e0b",   cancelled: "#ef4444", draft: "#a78bfa",
};
const STATUS_LABELS = {
  active: "Activa", scheduled: "Programada", ended: "Finalizada",
  sold: "Vendida",  cancelled: "Cancelada",  draft: "Borrador",
};
const STATUS_BG = {
  active:    "bg-green-100 text-green-700",
  scheduled: "bg-blue-100 text-blue-700",
  ended:     "bg-gray-100 text-gray-500",
  sold:      "bg-amber-100 text-amber-700",
  cancelled: "bg-red-100 text-red-500",
  draft:     "bg-purple-100 text-purple-600",
};

const ORDER_COLORS = {
  pendiente:  "#f59e0b",
  confirmado: "#3b82f6",
  asignado:   "#8b5cf6",
  listo:      "#10b981",
  en_camino:  "#06b6d4",
  entregado:  "#22c55e",
  cancelado:  "#ef4444",
};
const ORDER_LABELS = {
  pendiente:  "Pendiente",
  confirmado: "Confirmado",
  asignado:   "Asignado",
  listo:      "Listo",
  en_camino:  "En camino",
  entregado:  "Entregado",
  cancelado:  "Cancelado",
};
const ORDER_BG = {
  pendiente:  "bg-amber-100 text-amber-700",
  confirmado: "bg-blue-100 text-blue-700",
  asignado:   "bg-violet-100 text-violet-700",
  listo:      "bg-emerald-100 text-emerald-700",
  en_camino:  "bg-cyan-100 text-cyan-700",
  entregado:  "bg-green-100 text-green-700",
  cancelado:  "bg-red-100 text-red-500",
};

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/api\/?$/, "");

// ── Sub-componentes ────────────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, bg, sub, trend }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${bg}`}>
          <Icon size={20} className="text-white" />
        </div>
        {trend != null && (
          <span className="flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full">
            <TrendingUp size={11} /> {trend}
          </span>
        )}
      </div>
      <div>
        <p className="text-3xl font-black text-gray-800">
          {value === null ? <span className="text-xl text-gray-400">—</span> : value.toLocaleString("es-CO")}
        </p>
        <p className="text-sm font-medium text-gray-500 mt-0.5">{label}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function TimelineItem({ auction }) {
  const icons = {
    active:    <CheckCircle2 size={15} className="text-green-500" />,
    scheduled: <Clock        size={15} className="text-blue-500" />,
    ended:     <CheckCircle2 size={15} className="text-gray-400" />,
    cancelled: <XCircle      size={15} className="text-red-400" />,
    draft:     <AlertCircle  size={15} className="text-purple-400" />,
    sold:      <CheckCircle2 size={15} className="text-amber-500" />,
  };
  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-50 last:border-0">
      <div className="mt-0.5 shrink-0">{icons[auction.status] ?? icons.ended}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 truncate">{auction.title}</p>
        <p className="text-xs text-gray-400 mt-0.5">
          {auction.company?.name} · {dayjs(auction.createdAt).fromNow()}
        </p>
      </div>
      <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_BG[auction.status] ?? "bg-gray-100 text-gray-500"}`}>
        {STATUS_LABELS[auction.status] ?? auction.status}
      </span>
    </div>
  );
}

function OrderLogItem({ order }) {
  const icon = {
    pendiente:  <Clock        size={15} className="text-amber-500" />,
    confirmado: <CheckCircle2 size={15} className="text-blue-500" />,
    asignado:   <Truck        size={15} className="text-violet-500" />,
    listo:      <CheckCircle2 size={15} className="text-emerald-500" />,
    en_camino:  <Truck        size={15} className="text-cyan-500" />,
    entregado:  <CheckCircle2 size={15} className="text-green-500" />,
    cancelado:  <XCircle      size={15} className="text-red-400" />,
  };
  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-50 last:border-0">
      <div className="mt-0.5 shrink-0">{icon[order.status] ?? <Package size={15} className="text-gray-400" />}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs font-bold text-gray-700">#{order.orderNumber?.slice(-6) ?? order.id?.slice(-6).toUpperCase()}</span>
          <span className="text-xs text-gray-400">·</span>
          <span className="text-xs text-gray-600 truncate">{order.user?.name ?? "Usuario"}</span>
          <span className="text-xs text-gray-400">→</span>
          <span className="text-xs font-medium text-gray-700 truncate">{order.company?.name ?? "Tienda"}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-gray-400">{dayjs(order.createdAt).fromNow()}</span>
          {order.ryder?.user?.name && (
            <span className="text-xs text-violet-500">· Ryder: {order.ryder.user?.name}</span>
          )}
        </div>
      </div>
      <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-semibold ${ORDER_BG[order.status] ?? "bg-gray-100 text-gray-500"}`}>
        {ORDER_LABELS[order.status] ?? order.status}
      </span>
    </div>
  );
}

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white shadow-lg border border-gray-100 rounded-xl px-3 py-2 text-sm">
      <p className="font-bold text-gray-700">{payload[0].name ?? payload[0].dataKey}</p>
      <p className="text-supernova-600 font-semibold">{payload[0].value}</p>
    </div>
  );
};

// ── Admin Orders ───────────────────────────────────────────────────────────────

const ACTIVE_STATUSES = ["pendiente", "confirmado", "asignado", "listo", "en_camino"];

function RyderDropdown({ orderId, ryders, currentRyderId, onAssigned }) {
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const anchorRef = useRef(null);

  const assign = async (ryderId) => {
    setLoading(true);
    setOpen(false);
    try {
      await API.patch(`/orders/${orderId}/assign-ryder`, { ryderId });
      toast.success("Ryder reasignado correctamente");
      onAssigned(orderId, ryderId);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al reasignar");
    } finally { setLoading(false); }
  };

  const available = ryders.filter(r => r.approvalStatus === "aprobado");

  return (
    <div className="inline-block">
      <button
        ref={anchorRef}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        disabled={loading}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-supernova-500 hover:bg-supernova-600 text-white text-xs font-bold rounded-lg transition disabled:opacity-60"
      >
        {loading ? <RefreshCw size={11} className="animate-spin" /> : <UserCheck size={11} />}
        {currentRyderId ? "Reasignar" : "Asignar ryder"}
        <ChevronDown size={11} />
      </button>
      <DropdownPortal
        open={open}
        anchorRef={anchorRef}
        onClose={() => setOpen(false)}
        className="bg-white border border-gray-200 rounded-xl shadow-xl max-h-52 overflow-y-auto"
      >
        {available.length === 0 ? (
          <p className="text-xs text-gray-400 px-3 py-3 text-center">Sin ryders disponibles</p>
        ) : (
          available.map(r => (
            <button
              key={r.id}
              onClick={(e) => { e.stopPropagation(); assign(r.id); }}
              className={`w-full text-left px-3 py-2.5 text-xs hover:bg-supernova-50 flex items-center gap-2 transition ${r.id === currentRyderId ? "bg-supernova-50 font-bold text-supernova-700" : "text-gray-700"}`}
            >
              <div className="w-6 h-6 rounded-full bg-supernova-100 flex items-center justify-center text-supernova-700 font-bold text-xs shrink-0">
                {(r.name || "R")[0].toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="font-semibold truncate">{r.name}</p>
                <p className="text-gray-400 truncate">{r.phone || ""}</p>
              </div>
              {r.id === currentRyderId && <CheckCircle2 size={12} className="text-supernova-500 ml-auto shrink-0" />}
            </button>
          ))
        )}
      </DropdownPortal>
    </div>
  );
}

// ── Order detail helpers ───────────────────────────────────────────────────────

function DetailSection({ title, icon: Icon, children }) {
  return (
    <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Icon size={14} className="text-gray-400" />
        <h4 className="text-xs font-black uppercase tracking-wider text-gray-500">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function DetailRow({ label, value, isPhone }) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className="text-gray-400 shrink-0">{label}</span>
      {isPhone && value && value !== "—" ? (
        <a href={`tel:${value}`} className="font-semibold text-indigo-600 hover:text-indigo-700">{value}</a>
      ) : (
        <span className="font-semibold text-gray-700 text-right">{value}</span>
      )}
    </div>
  );
}

const ORDER_TIMELINE_STEPS = [
  { key: "createdAt",            label: "Pedido creado",          color: "bg-gray-400" },
  { key: "confirmedAt",          label: "Confirmado por la tienda", color: "bg-blue-400" },
  { key: "assignedAt",           label: "Domiciliario asignado",  color: "bg-violet-400" },
  { key: "pickupCodeVerifiedAt", label: "Código de recogida verificado", color: "bg-amber-400" },
  { key: "deliveredAt",          label: "Entregado al cliente",   color: "bg-green-500" },
];

function OrderDetailPanel({ orderId, onClose, ryders, onAssigned }) {
  const [order,   setOrder]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    setOrder(null);
    API.get(`/orders/${orderId}`)
      .then(r => {
        // sendSuccess → { success, message, data: { order: {...} } }
        const payload = r.data?.data ?? r.data;
        const extracted = payload?.order ?? payload;
        console.log("[AdminOrders] order detail:", extracted);
        setOrder(extracted);
      })
      .catch(err => {
        console.error("[AdminOrders] error fetching order:", err?.response?.data ?? err);
        toast.error("No se pudo cargar el pedido");
      })
      .finally(() => setLoading(false));
  }, [orderId]);

  const isActive   = order && ACTIVE_STATUSES.includes(order.status);
  const photoUrl   = order?.deliveryPhoto ? `${API_BASE}/uploads/delivery/${order.deliveryPhoto}` : null;
  const ryderLat   = order?.ryder?.lat ? Number(order.ryder.lat) : null;
  const ryderLng   = order?.ryder?.lng ? Number(order.ryder.lng) : null;
  const hasRyderPos = isActive && ryderLat && ryderLng;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      {/* Slide panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-xl z-50 bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0 bg-white">
          <div className="flex items-center gap-3 min-w-0">
            <Package size={18} className="text-gray-400 shrink-0" />
            {order ? (
              <>
                <span className="font-black text-gray-800 text-sm">#{order.orderNumber}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${ORDER_BG[order.status] ?? "bg-gray-100 text-gray-500"}`}>
                  {ORDER_LABELS[order.status] ?? order.status}
                </span>
              </>
            ) : (
              <span className="text-sm text-gray-400">Cargando pedido…</span>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition shrink-0">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-5 space-y-3 animate-pulse">
              {[20, 16, 24, 16, 20].map((h, i) => (
                <div key={i} className={`h-${h} bg-gray-100 rounded-xl`} />
              ))}
            </div>
          )}

          {!loading && order && (
            <div className="p-5 space-y-4">

              {/* Cliente */}
              <DetailSection title="Cliente" icon={Users}>
                <DetailRow label="Nombre" value={order.user?.name ?? "—"} />
                <DetailRow label="Email"  value={order.user?.email ?? "—"} />
                <DetailRow label="Teléfono" value={order.deliveryPhone ?? order.user?.phone ?? "—"} isPhone />
                {/* Para "recoger en tienda" el backend guarda la dirección de la SEDE en
                    deliveryAddress (es el punto de retiro, no la dirección del cliente) —
                    mostrarla acá bajo "Dirección" sería atribuírsela al cliente por error.
                    Ese caso ya tiene su propio bloque "Retiro en tienda" más abajo. */}
                {order.deliveryType !== "recoger_en_tienda" && order.deliveryAddress && (
                  <DetailRow label="Dirección" value={order.deliveryAddress} />
                )}
                {order.deliveryType !== "recoger_en_tienda" && order.deliveryCity && (
                  <DetailRow label="Ciudad" value={order.deliveryCity} />
                )}
                {order.notes && <DetailRow label="Notas" value={order.notes} />}
              </DetailSection>

              {order.deliveryType === "recoger_en_tienda" && (
                <DetailSection title="Retiro en tienda" icon={MapPin}>
                  <DetailRow label="Sede" value={(order.pickupSede ?? order.sede)?.name ?? "—"} />
                  <DetailRow label="Dirección" value={(order.pickupSede ?? order.sede)?.address ?? "—"} />
                </DetailSection>
              )}

              {/* Tienda */}
              <DetailSection title="Tienda" icon={Building2}>
                <div className="flex items-center gap-3">
                  {order.company?.logo && (
                    <img src={order.company.logo} alt="" className="w-10 h-10 rounded-xl object-cover border border-gray-100 shrink-0" />
                  )}
                  <div>
                    <p className="text-sm font-bold text-gray-800">{order.company?.name ?? "—"}</p>
                    {order.sede?.name && (
                      <p className="text-xs text-gray-400 mt-0.5">{order.sede.name} · {order.sede.address}</p>
                    )}
                  </div>
                </div>
              </DetailSection>

              {/* Productos */}
              <DetailSection title="Detalle del pedido" icon={Package}>
                <div className="space-y-2.5">
                  {order.items?.map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-gray-200 text-gray-600 text-[10px] font-black flex items-center justify-center shrink-0 mt-0.5">{item.quantity}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-800 leading-tight">{item.title ?? item.productName ?? "Producto"}</p>
                        {(item.note || item.notes) && <p className="text-[10px] text-gray-400 mt-0.5 italic">{item.note ?? item.notes}</p>}
                      </div>
                      <p className="text-xs font-bold text-gray-700 shrink-0">
                        ${(Number(item.price ?? item.unitPrice ?? 0) * item.quantity).toLocaleString("es-CO")}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-200 pt-3 mt-1 space-y-1.5">
                  {order.subtotal != null && (
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Subtotal</span>
                      <span>${Number(order.subtotal).toLocaleString("es-CO")}</span>
                    </div>
                  )}
                  {order.deliveryFee != null && (
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Domicilio {order.deliveryDistanceKm ? `(${Number(order.deliveryDistanceKm).toFixed(1)} km)` : ""}</span>
                      <span>${Number(order.deliveryFee).toLocaleString("es-CO")}</span>
                    </div>
                  )}
                  {Number(order.couponDiscount) > 0 && (
                    <div className="flex justify-between text-xs text-green-600">
                      <span>Cupón {order.couponCode ? `(${order.couponCode})` : ""}</span>
                      <span>-${Number(order.couponDiscount).toLocaleString("es-CO")}</span>
                    </div>
                  )}
                  {order.qpointsUsed > 0 && (
                    <div className="flex justify-between text-xs text-amber-600">
                      <span>Q-Puntos usados</span>
                      <span>-{order.qpointsUsed} pts</span>
                    </div>
                  )}
                  {order.total != null && (
                    <div className="flex justify-between text-sm font-black text-gray-800 pt-1 border-t border-gray-200">
                      <span>Total</span>
                      <span>${Number(order.total).toLocaleString("es-CO")}</span>
                    </div>
                  )}
                  {order.paymentMethod && (
                    <p className="text-[10px] text-gray-400 text-right capitalize">Pago: {order.paymentMethod}</p>
                  )}
                  {order.commissionAmount != null && (
                    <div className="mt-2 pt-2 border-t border-dashed border-gray-200 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400">Comisión Qkiere ({Number(order.commissionPercent ?? 0)}%)</span>
                        <span className="font-semibold text-supernova-600">${Number(order.commissionAmount).toLocaleString("es-CO")}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400">Ganancia de la tienda</span>
                        <span className="font-semibold text-green-600">${Number(order.companyNetAmount).toLocaleString("es-CO")}</span>
                      </div>
                    </div>
                  )}
                </div>
              </DetailSection>

              {/* Domiciliario */}
              <DetailSection title="Domiciliario" icon={Truck}>
                {order.ryder ? (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center text-violet-700 font-black text-base shrink-0">
                        {(order.ryder.user?.name || "R")[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-800">{order.ryder.user?.name}</p>
                        <p className="text-xs text-gray-400 capitalize truncate">
                          {[order.ryder.vehicleType, order.ryder.vehicleBrand, order.ryder.vehicleModel, order.ryder.vehiclePlate].filter(Boolean).join(" ")}
                        </p>
                      </div>
                      {order.ryder.user?.phone && (
                        <a
                          href={`tel:${order.ryder.user?.phone}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 border border-violet-200 rounded-lg text-violet-600 text-xs font-bold hover:bg-violet-100 transition shrink-0"
                        >
                          <Phone size={11} /> {order.ryder.user?.phone}
                        </a>
                      )}
                    </div>
                    {isActive && (
                      <div className="mt-2">
                        <RyderDropdown
                          orderId={order.id}
                          ryders={ryders}
                          currentRyderId={order.ryderId}
                          onAssigned={(oid, rid) => {
                            const r = ryders.find(x => x.id === rid);
                            setOrder(prev => ({ ...prev, ryderId: rid, status: "asignado", ryder: r }));
                            onAssigned(oid, rid);
                          }}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div>
                    <p className="text-xs text-gray-400 mb-2">Sin domiciliario asignado</p>
                    {isActive && (
                      <RyderDropdown
                        orderId={order.id}
                        ryders={ryders}
                        currentRyderId={null}
                        onAssigned={(oid, rid) => {
                          const r = ryders.find(x => x.id === rid);
                          setOrder(prev => ({ ...prev, ryderId: rid, status: "asignado", ryder: r }));
                          onAssigned(oid, rid);
                        }}
                      />
                    )}
                  </div>
                )}
              </DetailSection>

              {/* Timeline */}
              <DetailSection title="Historial de estados" icon={Clock}>
                <div className="space-y-3">
                  {ORDER_TIMELINE_STEPS.map(({ key, label, color }) => {
                    const ts = order[key];
                    if (!ts) return null;
                    return (
                      <div key={key} className="flex items-start gap-3">
                        <div className={`w-2.5 h-2.5 rounded-full mt-0.5 shrink-0 ${color}`} />
                        <div>
                          <p className="text-xs font-semibold text-gray-700">{label}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            {dayjs(ts).format("DD/MM/YYYY [a las] HH:mm:ss")}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </DetailSection>

              {/* Foto de entrega */}
              {photoUrl && (
                <DetailSection title="Foto de entrega" icon={CheckCircle2}>
                  <img
                    src={photoUrl}
                    alt="Foto de entrega"
                    className="w-full rounded-xl object-cover max-h-72 border border-gray-100"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                </DetailSection>
              )}

              {/* Mapa domiciliario (solo si pedido activo y tiene coordenadas) */}
              {hasRyderPos && (
                <DetailSection title="Ubicación del domiciliario" icon={MapPin}>
                  <div style={{ isolation: "isolate" }}>
                    <RyderLiveMap
                      position={{ lat: ryderLat, lng: ryderLng }}
                      vehicleType={order.ryder?.vehicleType}
                      deliveryLocation={
                        order.deliveryLat && order.deliveryLng
                          ? { lat: Number(order.deliveryLat), lng: Number(order.deliveryLng) }
                          : null
                      }
                    />
                  </div>
                  <p className="text-[10px] text-gray-400 text-center mt-1">
                    La ubicación se actualiza cuando el domiciliario mueve su teléfono
                  </p>
                </DetailSection>
              )}

            </div>
          )}
        </div>
      </div>
    </>
  );
}

function AdminOrders() {
  const [orders,     setOrders]     = useState([]);
  const [ryders,     setRyders]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState("active");
  const [search,     setSearch]     = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [ordRes, ryRes] = await Promise.allSettled([
        API.get("/orders/admin/all?limit=200"),
        API.get("/ryders"),
      ]);
      const ordData = ordRes.status === "fulfilled"
        ? (ordRes.value.data?.data?.orders ?? ordRes.value.data?.data ?? []) : [];
      const ryData  = ryRes.status === "fulfilled"
        ? (ryRes.value.data?.data?.data ?? ryRes.value.data?.data ?? ryRes.value.data ?? []) : [];
      setOrders(Array.isArray(ordData) ? ordData : []);
      setRyders(Array.isArray(ryData)  ? ryData  : []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleAssigned = (orderId, ryderId) => {
    setOrders(prev => prev.map(o => {
      if (o.id !== orderId) return o;
      const ryder = ryders.find(r => r.id === ryderId);
      return { ...o, ryderId, status: "asignado", ryder };
    }));
  };

  const q = search.trim().toLowerCase();
  const displayed = orders
    .filter(o => filter === "active" ? ACTIVE_STATUSES.includes(o.status) : true)
    .filter(o => !q || (
      o.orderNumber?.toLowerCase().includes(q) ||
      o.user?.name?.toLowerCase().includes(q) ||
      o.company?.name?.toLowerCase().includes(q)
    ));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-black text-gray-800">Pedidos</h2>
          <p className="text-sm text-gray-400">Haz clic en un pedido para ver todos los detalles</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por # o cliente…"
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-supernova-300 w-44"
          />
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
            {[["active", "Activos"], ["all", "Todos"]].map(([val, label]) => (
              <button key={val} onClick={() => setFilter(val)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${filter === val ? "bg-white shadow text-gray-800" : "text-gray-500 hover:text-gray-700"}`}>
                {label}
              </button>
            ))}
          </div>
          <button onClick={load} disabled={loading}
            className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-500 transition">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="space-y-2 p-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-14 rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : displayed.length === 0 ? (
          <div className="text-center py-14 text-gray-400">
            <Package size={36} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">{filter === "active" ? "Sin pedidos activos" : "Sin pedidos"}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {displayed.map(order => (
              <div
                key={order.id}
                onClick={() => setSelectedId(order.id)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-supernova-50/40 transition cursor-pointer group"
              >
                {/* Número + fecha */}
                <div className="shrink-0 w-20">
                  <p className="text-xs font-bold text-gray-700">
                    #{order.orderNumber?.slice(-7) ?? order.id?.slice(-6).toUpperCase()}
                  </p>
                  <p className="text-[10px] text-gray-400">{dayjs(order.createdAt).fromNow()}</p>
                </div>
                {/* Cliente → Tienda */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800 truncate">
                    {order.user?.name ?? "—"} → {order.company?.name ?? "—"}
                  </p>
                  {order.ryder?.user?.name ? (
                    <p className="text-[10px] text-violet-600 truncate flex items-center gap-1 mt-0.5">
                      <Truck size={9} /> {order.ryder.user?.name}
                    </p>
                  ) : (
                    <p className="text-[10px] text-gray-400 mt-0.5">Sin ryder</p>
                  )}
                </div>
                {/* Total */}
                {order.total != null && (
                  <span className="shrink-0 text-xs font-bold text-gray-700 hidden sm:block">
                    ${Number(order.total).toLocaleString("es-CO")}
                  </span>
                )}
                {/* Status */}
                <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-semibold ${ORDER_BG[order.status] ?? "bg-gray-100 text-gray-500"}`}>
                  {ORDER_LABELS[order.status] ?? order.status}
                </span>
                <ChevronRight size={14} className="text-gray-300 shrink-0 group-hover:text-supernova-400 transition" />
              </div>
            ))}
          </div>
        )}
      </div>
      <p className="text-xs text-gray-400 text-right">{displayed.length} pedido{displayed.length !== 1 ? "s" : ""}</p>

      {/* Detail panel */}
      {selectedId && (
        <OrderDetailPanel
          orderId={selectedId}
          onClose={() => setSelectedId(null)}
          ryders={ryders}
          onAssigned={handleAssigned}
        />
      )}
    </div>
  );
}

// ── Dashboard home ─────────────────────────────────────────────────────────────

function DashboardHome() {
  const [stats,    setStats]    = useState(null);
  const [auctions, setAuctions] = useState([]);
  const [orders,   setOrders]   = useState([]);
  const [loading,  setLoading]  = useState(true);

  const load = async () => {
    setLoading(true);
    const [usersRes, companiesRes, auctionsRes, rydersRes, ordersRes] = await Promise.allSettled([
      API.get("/users"),
      API.get("/companies"),
      API.get("/auctions/admin/all?limit=100"),
      API.get("/ryders"),
      API.get("/orders/admin/all?limit=50"),
    ]);

    // Users — total registrados
    const usersArr   = usersRes.status === "fulfilled"
      ? (usersRes.value.data?.data?.data ?? usersRes.value.data?.data ?? []) : [];
    const totalUsers = usersRes.status === "fulfilled"
      ? (usersRes.value.data?.data?.total ?? usersArr.length) : null;

    // Companies — total + activas
    const companiesArr   = companiesRes.status === "fulfilled"
      ? (companiesRes.value.data?.data?.data ?? companiesRes.value.data?.data ?? []) : [];
    const totalCompanies = companiesRes.status === "fulfilled"
      ? (companiesRes.value.data?.data?.total ?? companiesArr.length) : null;
    const activeCompanies = Array.isArray(companiesArr)
      ? companiesArr.filter((c) => c.status === "Activo").length : null;

    // Auctions
    const auctionData    = auctionsRes.status === "fulfilled" ? (auctionsRes.value.data?.data?.auctions ?? []) : [];
    const totalAuctions  = auctionsRes.status === "fulfilled" ? (auctionsRes.value.data?.data?.total ?? auctionData.length) : null;
    const activeAuctions = auctionData.filter((a) => a.status === "active").length;

    // Ryders — total + aprobados
    const rydersArr       = rydersRes.status === "fulfilled"
      ? (rydersRes.value.data?.data?.data ?? rydersRes.value.data?.data ?? rydersRes.value.data ?? []) : [];
    const totalRyders     = Array.isArray(rydersArr) ? rydersArr.length : null;
    const approvedRyders  = Array.isArray(rydersArr)
      ? rydersArr.filter((r) => r.approvalStatus === "aprobado").length : null;

    // Orders
    const orderData        = ordersRes.status === "fulfilled" ? (ordersRes.value.data?.data?.orders ?? []) : [];
    const totalOrders      = ordersRes.status === "fulfilled" ? (ordersRes.value.data?.data?.total ?? orderData.length) : null;
    const pendingOrders    = orderData.filter((o) => o.status === "pendiente").length;
    const inProgressOrders = orderData.filter((o) => ["confirmado","asignado","listo","en_camino"].includes(o.status)).length;
    const deliveredOrders  = orderData.filter((o) => o.status === "entregado").length;

    setStats({
      totalUsers, totalCompanies, activeCompanies,
      totalAuctions, activeAuctions,
      totalRyders, approvedRyders,
      totalOrders, pendingOrders, inProgressOrders, deliveredOrders,
    });
    setAuctions(auctionData);
    setOrders(orderData);
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const pieData = useMemo(() => {
    const map = {};
    auctions.forEach((a) => { map[a.status] = (map[a.status] || 0) + 1; });
    return Object.entries(map).map(([status, value]) => ({
      name: STATUS_LABELS[status] ?? status, value,
      color: STATUS_COLORS[status] ?? "#94a3b8",
    }));
  }, [auctions]);

  const trendData = useMemo(() => {
    const days = {};
    for (let i = 6; i >= 0; i--) days[dayjs().subtract(i, "day").format("DD/MM")] = 0;
    auctions.forEach((a) => {
      const d = dayjs(a.createdAt).format("DD/MM");
      if (d in days) days[d]++;
    });
    return Object.entries(days).map(([fecha, total]) => ({ fecha, total }));
  }, [auctions]);

  // Pedidos por estado para BarChart
  const ordersBarData = useMemo(() => {
    const map = {};
    orders.forEach((o) => { map[o.status] = (map[o.status] || 0) + 1; });
    return Object.entries(map).map(([status, total]) => ({
      estado: ORDER_LABELS[status] ?? status, total,
      fill: ORDER_COLORS[status] ?? "#94a3b8",
    }));
  }, [orders]);

  const KPI_MAIN = [
    { label: "Usuarios registrados", value: stats?.totalUsers,      icon: Users,     bg: "bg-blue-500" },
    { label: "Empresas totales",     value: stats?.totalCompanies,  icon: Building2, bg: "bg-violet-500" },
    { label: "Empresas activas",     value: stats?.activeCompanies, icon: Building2, bg: "bg-emerald-500", trend: stats?.totalCompanies > 0 ? `${Math.round((stats.activeCompanies / stats.totalCompanies) * 100)}%` : null },
    { label: "Ryders registrados",   value: stats?.totalRyders,     icon: Truck,     bg: "bg-orange-500" },
    { label: "Ryders aprobados",     value: stats?.approvedRyders,  icon: Truck,     bg: "bg-supernova-500", trend: stats?.totalRyders > 0 ? `${Math.round((stats.approvedRyders / stats.totalRyders) * 100)}%` : null },
  ];
  const KPI_ORDERS = [
    { label: "Total pedidos",   value: stats?.totalOrders,      icon: Package, bg: "bg-slate-500" },
    { label: "Pendientes",      value: stats?.pendingOrders,    icon: Clock,   bg: "bg-amber-500" },
    { label: "En proceso",      value: stats?.inProgressOrders, icon: Truck,   bg: "bg-cyan-500" },
    { label: "Entregados",      value: stats?.deliveredOrders,  icon: CheckCircle2, bg: "bg-green-500" },
  ];

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <div key={i} className="h-32 bg-gray-100 rounded-2xl" />)}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-gray-100 rounded-2xl" />)}
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => <div key={i} className="h-64 bg-gray-100 rounded-2xl" />)}
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="h-80 bg-gray-100 rounded-2xl" />
          <div className="h-80 bg-gray-100 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Refrescar */}
      <div className="flex justify-end">
        <button onClick={load}
          className="flex items-center gap-2 text-xs font-semibold text-gray-500 hover:text-gray-700 bg-white border border-gray-200 px-3 py-1.5 rounded-lg shadow-sm transition">
          <RefreshCw size={13} /> Actualizar
        </button>
      </div>

      {/* KPIs — plataforma */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Plataforma</p>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {KPI_MAIN.map((c) => <StatCard key={c.label} {...c} />)}
        </div>
      </div>

      {/* KPIs — pedidos */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Pedidos</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {KPI_ORDERS.map((c) => <StatCard key={c.label} {...c} />)}
        </div>
      </div>

      {/* Gráficas subastas */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-bold text-gray-700 mb-4">Subastas — últimos 7 días</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={trendData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
              <defs>
                <linearGradient id="gradA" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#f59e0b" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="fecha" tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="total" name="Subastas" stroke="#f59e0b" strokeWidth={2.5} fill="url(#gradA)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-bold text-gray-700 mb-4">Subastas por estado</h3>
          {pieData.length === 0 ? (
            <div className="flex items-center justify-center h-[200px] text-gray-300 text-sm">Sin datos</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} innerRadius={42} paddingAngle={3}>
                  {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v}`, n]} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 justify-center">
            {pieData.map((e) => (
              <span key={e.name} className="flex items-center gap-1.5 text-xs text-gray-500">
                <span className="w-2 h-2 rounded-full" style={{ background: e.color }} />{e.name} ({e.value})
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Gráfica pedidos por estado */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-bold text-gray-700 mb-4">Pedidos por estado</h3>
        {ordersBarData.length === 0 ? (
          <div className="flex items-center justify-center h-[180px] text-gray-300 text-sm">Sin pedidos</div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={ordersBarData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="estado" tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <Tooltip formatter={(v) => [`${v} pedidos`]} />
              <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                {ordersBarData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Logs lado a lado */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Timeline subastas */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-bold text-gray-700 mb-1">Últimas subastas</h3>
          <p className="text-xs text-gray-400 mb-3">10 más recientes</p>
          {auctions.length === 0
            ? <p className="text-sm text-gray-400 text-center py-6">Sin subastas</p>
            : auctions.slice(0, 10).map((a) => <TimelineItem key={a.id} auction={a} />)
          }
        </div>

        {/* Log pedidos */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-bold text-gray-700 mb-1">Últimos pedidos</h3>
          <p className="text-xs text-gray-400 mb-3">Usuario · Tienda · Ryder</p>
          {orders.length === 0
            ? <p className="text-sm text-gray-400 text-center py-6">Sin pedidos</p>
            : orders.slice(0, 10).map((o) => <OrderLogItem key={o.id} order={o} />)
          }
        </div>
      </div>

      {/* Resumen barras subastas */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-bold text-gray-700 mb-4">Distribución de subastas</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
          {Object.entries(STATUS_LABELS).map(([key, label]) => {
            const count = auctions.filter((a) => a.status === key).length;
            const pct = auctions.length > 0 ? Math.round((count / auctions.length) * 100) : 0;
            return (
              <div key={key}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-medium text-gray-600">{label}</span>
                  <span className="font-bold text-gray-800">{count}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${pct}%`, background: STATUS_COLORS[key] }} />
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-400 mt-4 text-right">Total cargadas: {auctions.length}</p>
      </div>
    </div>
  );
}

// ── Panel principal ────────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const [active, setActive] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const handleNav = (item) => {
    setActive(item.id);
    setMobileSidebar(false);
    setSelectedCompany(null);
  };

  const Sidebar = ({ mobile = false }) => (
    <aside className={`
      ${mobile ? "w-64" : sidebarOpen ? "w-64" : "w-16"}
      bg-gray-900 text-white flex flex-col transition-all duration-300
      ${mobile ? "" : "hidden lg:flex"}
    `}>
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-gray-800">
        <div className="w-9 h-9 bg-supernova-500 rounded-xl flex items-center justify-center shrink-0">
          <Gavel size={18} className="text-white" />
        </div>
        {(sidebarOpen || mobile) && (
          <div className="min-w-0">
            <p className="font-black text-white text-sm leading-tight">Qkiere</p>
            <p className="text-gray-400 text-xs">Admin Panel</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = active === item.id;
          return (
            <button
              key={item.id}
              onClick={() => handleNav(item)}
              title={!sidebarOpen && !mobile ? item.label : undefined}
              className={`
                w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-all
                ${isActive
                  ? "bg-supernova-500/20 text-supernova-400 border-r-2 border-supernova-400"
                  : "text-gray-400 hover:bg-gray-800 hover:text-white"}
              `}
            >
              <Icon size={18} className="shrink-0" />
              {(sidebarOpen || mobile) && <span className="truncate">{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* Toggle (solo desktop) */}
      {!mobile && (
        <div className="border-t border-gray-800 p-3">
          <button
            onClick={() => setSidebarOpen((v) => !v)}
            className="w-full flex items-center justify-center gap-2 text-xs text-gray-400 hover:text-white transition py-2 rounded-lg hover:bg-gray-800"
          >
            {sidebarOpen ? <X size={14} /> : <Menu size={14} />}
            {sidebarOpen && <span>Colapsar</span>}
          </button>
        </div>
      )}
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Sidebar desktop */}
      <Sidebar />

      {/* Overlay móvil */}
      {mobileSidebar && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileSidebar(false)}
        >
          <div className="flex h-full" onClick={(e) => e.stopPropagation()}>
            <Sidebar mobile />
            <button
              onClick={() => setMobileSidebar(false)}
              className="absolute top-4 right-4 text-white"
            >
              <X size={22} />
            </button>
          </div>
        </div>
      )}

      {/* Área principal */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-14 bg-white border-b border-gray-200 flex items-center px-4 gap-3 shrink-0">
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition"
            onClick={() => setMobileSidebar(true)}
          >
            <Menu size={20} className="text-gray-600" />
          </button>
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-sm text-gray-500 min-w-0">
            <span className={`font-medium ${selectedCompany ? "text-gray-400 hover:text-gray-700 cursor-pointer" : "text-gray-800"}`}
              onClick={() => selectedCompany && setSelectedCompany(null)}>
              {SECTION_LABELS[active] ?? "Panel"}
            </span>
            {selectedCompany && (
              <>
                <ChevronRight size={14} className="text-gray-300 shrink-0" />
                <span className="font-semibold text-gray-800 truncate">{selectedCompany.name}</span>
              </>
            )}
          </div>
          {active !== "dashboard" && !selectedCompany && (
            <button
              onClick={() => setActive("dashboard")}
              className="ml-auto text-xs text-gray-400 hover:text-gray-600 transition flex items-center gap-1"
            >
              ← Dashboard
            </button>
          )}
        </header>

        {/* Contenido */}
        <main className="flex-1 overflow-auto p-4 lg:p-6 pb-20 lg:pb-6">
          {active === "dashboard"   && <DashboardHome />}
          {active === "companies"   && !selectedCompany && (
            <CompanyManager onSelectCompany={setSelectedCompany} />
          )}
          {active === "companies"   && selectedCompany && (
            <AdminCompanyDetail
              company={selectedCompany}
              onBack={() => setSelectedCompany(null)}
            />
          )}
          {active === "users_admin" && <UserManager />}
          {active === "users"       && <UserClients />}
          {active === "categories"  && <CategoryManager />}
          {active === "qpoints"     && <QPointsManager />}
          {active === "qpoints_report" && <QPointsReport />}
          {active === "coupons"     && <AdminCoupons />}
          {active === "wheel"       && <WheelManager />}
          {active === "landing"     && <LandingManager />}
          {active === "auctions"    && <AdminAuctions />}
          {active === "ryders"      && <AdminRyders />}
          {active === "orders"      && <AdminOrders />}
          {active === "events"      && <EventManager />}
          {active === "whatsapp"       && <WhatsAppManager />}
          {active === "whatsapp_inbox" && <WhatsAppInbox />}
          {active === "notifications"  && <AdminNotifications />}
          {active === "payments"         && <PaymentMethodsManager />}
          {active === "delivery_pricing" && <DeliveryPricingManager />}
          {active === "qr_generator"    && <QRGenerator />}
          {active === "popup_banner"   && <PopupBannerManager />}
          {active === "prospects"      && <ProspectManager />}
          {active === "marketing"      && <MarketingCampaigns />}
          {active === "marketing_ai"   && <MarketingAiConfig />}
          {active === "memberships"    && <MembershipPlans />}
          {active === "settlements"    && <SettlementsManager />}
        </main>
      </div>

      <MobileBottomBar />
    </div>
  );
}
